import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Icon } from '../../../../../blocks/icons';
import styles from './InvoiceEditFileSelectorDisplayText.module.scss';
import Globals from '../../../../../lib/Globals';
import { DocumentNode, gql } from '@apollo/client';
import {
	InvoiceEditFileSelectorDisplayTextInvoiceFragment,
	InvoiceStatus,
} from '#types';

const invoiceEditFileSelectorDisplayTextInvoiceFragment = gql`
	fragment invoiceEditFileSelectorDisplayTextInvoice on Invoice {
		uploadAttachment
		status
	}
`;

export type InvoiceEditFileSelectorDisplayTextProps = {
	invoice?: InvoiceEditFileSelectorDisplayTextInvoiceFragment;
	filename?: string | null | undefined;
	file?: File | string | undefined;
	setFile: ( file?: File ) => void;
	intentToChangeOrDeleteCurrentAttachmentFile: boolean;
	setIntentToChangeOrDeleteCurrentAttachmentFile: React.Dispatch<
	React.SetStateAction<boolean>
	>;
};

const attachmentUneditableStatuses = [
	InvoiceStatus.Disputed,
	InvoiceStatus.Void,
	InvoiceStatus.Hidden,
];

export const InvoiceEditFileSelectorDisplayText: React.FC<InvoiceEditFileSelectorDisplayTextProps> & {
	fragments: {
		invoiceEditFileSelectorDisplayTextInvoiceFragment: DocumentNode;
	};
} = ( {
	invoice,
	filename,
	file,
	setFile,
	intentToChangeOrDeleteCurrentAttachmentFile,
	setIntentToChangeOrDeleteCurrentAttachmentFile,
} ) => {
	const ref = React.useRef<HTMLInputElement>( null );
	const isEditable =
		!invoice ||
		( invoice?.status &&
			!attachmentUneditableStatuses.includes( invoice?.status ) );
	const filenameToShow = filename || invoice?.uploadAttachment;
	const canShowAttachmentName =
		filenameToShow && ( !intentToChangeOrDeleteCurrentAttachmentFile || file );
	return (
		<Box className={ styles.container }>
			{ canShowAttachmentName ? (
				<Box className={ styles.attached }>
					<Typography>{ filename || invoice?.uploadAttachment }</Typography>
					<IconButton
						aria-label='Delete attachment'
						className={ styles.iconButton }
						onClick={ () => {
							setIntentToChangeOrDeleteCurrentAttachmentFile( true );
							setFile( undefined );
						} }
					>
						<Icon type='trash' className={ styles.icon } />
					</IconButton>
				</Box>
			) : (
				<>
					<input
						data-cy='invoice-attachment-select'
						ref={ ref }
						className={ styles.input }
						disabled={ !isEditable }
						type='file'
						accept={ Globals.acceptedInvoiceAttachmentFileTypes.join( ', ' ) }
						onChange={ ( e ) => {
							if ( e.target.files?.[ 0 ] && isEditable ) {
								ref.current?.blur();
								setFile( e.target.files[ 0 ] );
								setIntentToChangeOrDeleteCurrentAttachmentFile( true );
							}
						} }
					/>
					<Button
						variant='text'
						aria-label='Add Attachment'
						className={ styles.addAttachment }
						disabled={ !isEditable }
						onClick={ () => {
							const mouseEvent = new MouseEvent( 'click', {
								bubbles: true,
								cancelable: true,
							} );
							setIntentToChangeOrDeleteCurrentAttachmentFile( true );
							ref.current?.dispatchEvent( mouseEvent );
						} }
						endIcon={
							isEditable && canShowAttachmentName ? (
								<Icon type='trash' className={ styles.icon } />
							) : null
						}
					>
						{ canShowAttachmentName
							? filenameToShow || 'Add Attachment'
							: 'Add Attachment' }
					</Button>
				</>
			) }
		</Box>
	);
};

InvoiceEditFileSelectorDisplayText.fragments = {
	invoiceEditFileSelectorDisplayTextInvoiceFragment,
};
